import * as React from 'react';
import { Reducer } from 'redux';
// reducers
import { ConfigReducer, DictionariesReducer, EmbeddedAppConfigReducer } from './reducers';
// types
import IConfig, { IBootstrapConfig } from '@sitegen/common/constants/config';
import { IBootstrap, IBootstrapAPI, IBootstrapOptions } from '@sitegen/common/types';
import { IWidgetExports } from 'types/widgets';
// services
import BootstrapAPI from './bootstrap-api.service';
import { configureServices } from './bootstrap-configure';

import { WIDGET_NAME, WIDGET_CONFIG, WIDGET_TYPE } from '@sitegen/common/constants';

const { reducerKey: configReducerKey, reducerHOF: configReducerHOF } = ConfigReducer;
const { reducerKey: dictionariesReducerKey, reducerHOF: dictionariesReducerHOF } = DictionariesReducer;
const { reducerKey: embeddedAppConfigReducerKey, reducerHOF: embeddedAppConfigReducerHOF } = EmbeddedAppConfigReducer;

const bootstrap: IBootstrap = async (
    bootstrapConfig: IBootstrapConfig,
    bootstrapOptions: IBootstrapOptions = {}
): Promise<IBootstrapAPI> => {
    const { dictionaries, ...applicationConfig } = bootstrapConfig;
    const isPreviewMode: boolean = !!bootstrapOptions.previewMode;

    let initialReducers: object = {
        [configReducerKey]: configReducerHOF(applicationConfig),
        [dictionariesReducerKey]: dictionariesReducerHOF(dictionaries),
        [embeddedAppConfigReducerKey]: embeddedAppConfigReducerHOF(bootstrapOptions.embeddedAppConfig)
    };

    const modules: IWidgetExports[] = await loadAndConfigureWidgets(applicationConfig);
    await configureServices(bootstrapConfig, isPreviewMode);

    /* Preparing widgets components, epics and reducers */
    const widgetsComponents: JSX.Element[] = modules.map((module, key) => <module.Container key={key} />);
    const widgetsRootEpics = modules.map(({ RootEpic }) => RootEpic);
    const widgetsReducers: Reducer[] = modules
        .map(({ Reducers, ReducersMock }) => (isPreviewMode ? ReducersMock : Reducers))
        .reduce((reducers, widgetReducers) => {
            return {
                ...reducers,
                ...widgetReducers
            };
        }, initialReducers) as Reducer[];

    return new BootstrapAPI({
        widgetsComponents,
        widgetsRootEpics,
        widgetsReducers
    });
};

/** Load widget code and perform basic configuration */
const loadAndConfigureWidgets = async (config: IConfig): Promise<IWidgetExports[]> => {
    const widgets: Promise<IWidgetExports>[] = [config.widgetName].map((name: WIDGET_NAME): Promise<IWidgetExports> => {
        const widgetType: WIDGET_TYPE = WIDGET_CONFIG[name].widgetType;
        return import(
            /* webpackExclude: /promotab/ */
            /* webpackChunkName: "[request]" */ `../widgets/${widgetType}/root/index`
        );
    });

    const modules: IWidgetExports[] = await Promise.all(widgets);
    const configureAll: Promise<void>[] = modules.map((module: IWidgetExports): Promise<void> => {
        return module.configureWidget(config.widgetName);
    });
    await Promise.all(configureAll);

    return modules;
};

export default bootstrap;
